import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { Spin } from 'antd';
import {
  MailOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  SettingOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import { StatsCardsProps } from './types';
import {
  STATUS_STATS,
  TEMPLATE_CATEGORIES,
  STAT_CARDS_LOADING_COUNT,
  BREAKDOWN_TITLE,
  NO_BREAKDOWN_TEXT,
  getStatusStatValue,
  extractStatsData,
  calculateCategoryStats,
  getBreakdownItems,
} from './constants';
import * as S from './styled';

const ICON_MAP: { [key: string]: React.ReactNode } = {
  MailOutlined: <MailOutlined />,
  CheckCircleOutlined: <CheckCircleOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  CloseCircleOutlined: <CloseCircleOutlined />,
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  UserOutlined: <UserOutlined />,
  SettingOutlined: <SettingOutlined />,
  GiftOutlined: <GiftOutlined />,
};

const StatsCardsWithTheme: React.FC<StatsCardsProps> = ({
  stats,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();

  const statsData = useMemo(() => extractStatsData(stats), [stats]);
  const byTypeData = useMemo(
    () => statsData.byType || {},
    [statsData]
  );
  const categoryStats = useMemo(
    () => calculateCategoryStats(byTypeData),
    [byTypeData]
  );
  const breakdownItems = useMemo(
    () => getBreakdownItems(byTypeData),
    [byTypeData]
  );

  if (loading) {
    return (
      <S.StatsGrid>
        {Array.from({ length: STAT_CARDS_LOADING_COUNT }).map((_, i) => (
          <S.LoadingCard key={i} isDarkMode={isDarkMode}>
            <Spin size="large" />
          </S.LoadingCard>
        ))}
      </S.StatsGrid>
    );
  }

  const renderStatCard = (
    label: string,
    value: number,
    icon: React.ReactNode,
    color: string,
    helpText: string,
    key: string
  ) => (
    <S.StatCard key={key} isDarkMode={isDarkMode}>
      <S.StatLabel isDarkMode={isDarkMode}>{label}</S.StatLabel>
      <S.StatNumber color={color}>{value}</S.StatNumber>
      <S.StatHelpText isDarkMode={isDarkMode}>
        <S.IconWrapper>{icon}</S.IconWrapper>
        {helpText}
      </S.StatHelpText>
    </S.StatCard>
  );

  const hasAnyBreakdown = breakdownItems.some((item) => item.count > 0);

  return (
    <S.Container isDarkMode={isDarkMode}>
      <S.StatsGrid>
        {STATUS_STATS.map((stat) =>
          renderStatCard(
            stat.label,
            getStatusStatValue(statsData, stat.key),
            ICON_MAP[stat.icon],
            stat.color,
            stat.helpText,
            stat.key
          )
        )}
      </S.StatsGrid>

      {/* Category Statistics */}
      <S.StatsGrid>
        {renderStatCard(
          TEMPLATE_CATEGORIES.ORDER.label,
          categoryStats.orderTemplates,
          ICON_MAP[TEMPLATE_CATEGORIES.ORDER.icon],
          TEMPLATE_CATEGORIES.ORDER.color,
          TEMPLATE_CATEGORIES.ORDER.helpText,
          'order'
        )}
        {renderStatCard(
          TEMPLATE_CATEGORIES.USER.label,
          categoryStats.userTemplates,
          ICON_MAP[TEMPLATE_CATEGORIES.USER.icon],
          TEMPLATE_CATEGORIES.USER.color,
          TEMPLATE_CATEGORIES.USER.helpText,
          'user'
        )}
        {renderStatCard(
          TEMPLATE_CATEGORIES.MERCHANT.label,
          categoryStats.merchantTemplates,
          ICON_MAP[TEMPLATE_CATEGORIES.MERCHANT.icon],
          TEMPLATE_CATEGORIES.MERCHANT.color,
          TEMPLATE_CATEGORIES.MERCHANT.helpText,
          'merchant'
        )}
        {renderStatCard(
          TEMPLATE_CATEGORIES.LOYALTY.label,
          categoryStats.loyaltyTemplates,
          ICON_MAP[TEMPLATE_CATEGORIES.LOYALTY.icon],
          TEMPLATE_CATEGORIES.LOYALTY.color,
          TEMPLATE_CATEGORIES.LOYALTY.helpText,
          'loyalty'
        )}
      </S.StatsGrid>

      {breakdownItems.length > 0 && (
        <S.BreakdownContainer isDarkMode={isDarkMode}>
          <S.BreakdownTitle isDarkMode={isDarkMode}>
            {BREAKDOWN_TITLE}
          </S.BreakdownTitle>
          {!hasAnyBreakdown ? (
            <S.EmptyState isDarkMode={isDarkMode}>
              {NO_BREAKDOWN_TEXT}
            </S.EmptyState>
          ) : (
            <S.BreakdownGrid>
              {breakdownItems.map((item) => {
                if (item.count === 0) return null;
                return (
                  <S.BreakdownItem key={item.type} isDarkMode={isDarkMode}>
                    <S.BreakdownLabel isDarkMode={isDarkMode}>
                      {item.label}
                    </S.BreakdownLabel>
                    <S.BreakdownCount>{item.count}</S.BreakdownCount>
                  </S.BreakdownItem>
                );
              })}
            </S.BreakdownGrid>
          )}
        </S.BreakdownContainer>
      )}
    </S.Container>
  );
};

const StatsCards: React.FC<StatsCardsProps> = (props) => (
  <ThemeProvider theme={{ mode: props as any }}>
    <StatsCardsWithTheme {...props} />
  </ThemeProvider>
);

export default StatsCards;
export { StatsCardsWithTheme };
